import { useState } from "react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { EXPERTISE } from "@/data/content";

const ABOUT_PHOTOS = [
  { image: "/images/about-hero-comms.jpeg", caption: "On comms — show operations mode" },
  { image: "/images/sovan-portrait.webp", caption: "Sovan Gourav Rout — Live Sound Engineer" },
  { image: "/images/console-work.jpeg", caption: "At the desk — large-format console, live show" },
];

export default function About() {
  const [viewerIndex, setViewerIndex] = useState(null);
  return (
    <section id="about" data-testid="about-section" className="relative overflow-hidden py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <div className="relative pl-10 sm:pl-14">
          <div className="absolute -left-10 -top-10 h-72 w-72 rounded-full bg-electric/15 blur-3xl" aria-hidden="true" />
          <Reveal className="relative ml-auto w-4/5">
            <button
              type="button"
              onClick={() => setViewerIndex(0)}
              data-testid="about-photo-main"
              aria-label="View photo full-screen"
              className="block w-full cursor-zoom-in"
            >
              <img
                src="/images/about-hero-comms.jpeg"
                alt="Sovan Gourav Rout on comms headset during show operations"
                data-testid="about-portrait"
                loading="lazy"
                className="max-h-[560px] w-full rounded-2xl border border-white/10 object-cover transition-[border-color] duration-300 hover:border-electric/50"
              />
            </button>
          </Reveal>
          <Reveal delay={0.1} className="absolute -left-0 top-14 z-10 w-2/5 sm:top-20">
            <button
              type="button"
              onClick={() => setViewerIndex(1)}
              data-testid="about-photo-secondary"
              aria-label="View photo full-screen"
              className="block w-full cursor-zoom-in"
            >
              <img
                src="/images/sovan-portrait.webp"
                alt="Portrait of Sovan Gourav Rout"
                data-testid="about-portrait-secondary"
                loading="lazy"
                className="-rotate-3 rounded-2xl border border-white/10 object-cover shadow-2xl shadow-black/60 transition-[transform,border-color] duration-500 hover:rotate-0 hover:border-electric/50"
              />
            </button>
          </Reveal>
          <Reveal delay={0.15} className="relative z-10 -mt-20 ml-auto w-3/5 sm:-mt-28">
            <button
              type="button"
              onClick={() => setViewerIndex(2)}
              data-testid="about-photo-console"
              aria-label="View photo full-screen"
              className="block w-full cursor-zoom-in"
            >
              <img
                src="/images/console-work.jpeg"
                alt="Sovan operating a large-format digital mixing console at a live show"
                data-testid="about-console-photo"
                loading="lazy"
                className="rotate-2 rounded-2xl border border-white/10 shadow-2xl shadow-black/60 transition-[transform,border-color] duration-500 hover:rotate-0 hover:border-electric/50"
              />
            </button>
          </Reveal>
        </div>

        <div>
          <SectionHeading
            label="About"
            title="The engineer behind"
            accent="the console"
            testId="about-heading"
          />
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-neutral-400 sm:text-lg">
              I'm Sovan Gourav Rout — a live sound engineer and audio specialist who has built a career on
              stages across the UAE and India. I currently work as an Audio Technician with{" "}
              <span className="font-semibold text-neutral-200">AgorA LLC</span>, part of the stage & show
              operations team at <span className="font-semibold text-neutral-200">Al Wasl Plaza, Expo City Dubai</span>,
              handling show setup, equipment preparation, rack building, maintenance and technical support.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-5 text-base leading-relaxed text-neutral-400 sm:text-lg">
              Alongside that, I freelance across concerts, festivals and corporate productions — engineering
              audio for arena headliners, orchestras, exhibitions and conferences where there are no second takes.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-3" data-testid="about-expertise-chips">
            {EXPERTISE.map((item, i) => (
              <Reveal key={item} delay={0.05 * i}>
                <span className="inline-block rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-neutral-300 transition-[border-color,color] duration-300 hover:border-electric/60 hover:text-electric">
                  {item}
                </span>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-10 flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="about-current-role-card">
              <img src="/images/agora-logo.webp" alt="AgorA LLC logo" loading="lazy" className="h-12 w-12 rounded-lg bg-white object-contain p-1.5" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-electric">Currently</p>
                <p className="font-display text-lg font-semibold text-neutral-100">Audio Technician — AgorA LLC</p>
                <p className="text-sm text-neutral-500">Sep 2024 – Present · Al Wasl Plaza, Expo City Dubai</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
      <Lightbox items={ABOUT_PHOTOS} index={viewerIndex} onClose={() => setViewerIndex(null)} onNavigate={setViewerIndex} />
    </section>
  );
}
